import { eq } from "drizzle-orm";
import { type PowerSyncDrizzleDB, user } from "@bso/powersync";
import { patchRowVerified } from "../powersync/verified-write";

/**
 * ── Why ALL mutations below use `.where(eq(user.id, userId))` ─
 *
 * Four of them (`locale`, `global_theme_id`, `is_blur`,
 * `follow_device_theme`) previously had this form:
 *
 *     const query = db.update(user).set({ … });
 *     await (userId === VISITOR_ID ? query : query.where(eq(user.id, userId)));
 *
 * In other words, in guest mode the UPDATE went out **without `WHERE`** → it rewrote EVERY
 * row in the local `user` table. The branch existed for symmetry with
 * `getUserThemeSettingsQuery`, which reads guest data with `.limit(1)` and no
 * filter; but a read-side `limit(1)` and an unfiltered UPDATE are not the same
 * bet: the former can select the wrong row, while the latter overwrites them all.
 *
 * The premise behind it ("the guest database has only one
 * `user`) is false in a concrete window: during guest -> real upgrade
 * (`flushGuestDataToServer`), the guest database CONNECTS to the
 * real account's connector and self-subscribes to every stream—including
 * `organizations`, which syncs one `user` row for EACH co-member of the
 * organizations in that account (`infra/sync_streams.yaml`). The table then has
 * 1 + 1 + N rows, `user` is NOT `localOnly`, and every touched row enqueues its
 * propio op en `ps_crud`.
 *
 * The correct filter is NOT guessed: the guest row is seeded by
 * `ensureUserSettingsRow(db, uid)` with `id = uid`, and for a guest tab
 * `uid` is exactly `VISITOR_ID`. Thus `eq(user.id, userId)` points to the
 * SAME row that the branch without a filter intended to touch when the table had a
 * single row—identical behavior in the normal case, bounded in all others.
 * This is also the form already used by `updateUserFontFamilyMutation` and
 * `updateUserImageMutation`, neither of which had a guest branch.
 *
 * Guarded by `packages/shared/src/users/__tests__/mutations-where.test.ts`.
 */

/**
 * ── Why these mutations DO go through `patchRowVerified` (see
 * `../powersync/verified-write.ts`) ─────────────────────────────────────────
 *
 * The original exclusion said: "`ensureUserSettingsRow` is a safety
 * net for the race between tabs, not the normal path—during
 * normal startup the row had ALREADY arrived via sync before `ThemeProvider` looked
 * for it." Audit 2026-08-13
 * (`infra/guides/POWERSYNC_VERIFIED_WRITE.md` §4.1,
 * `infra/reports/user-exclusion-audit/2026-08-13-summary.md`): esa premisa
 * was never verified against the code and is FALSE as a structural guarantee.
 *
 * Verified in this repo, not assumed:
 *
 * - `db` (the PowerSync accessor in `power-sync-context.tsx`) becomes
 *   no-nulo en cuanto `getOrCreateDbForUser` abre el SQLite local
 *   (`setDb(instance.db)`)—BEFORE the subsequent `connect()` finishes the
 *   sync inicial. No espera a `status().hasSynced`.
 * - `userId()`/`seedUserId()` in `theme-provider.tsx` activates as soon as
 *   `signUpActor`/`signInActor` (`auth/controllers.ts`) receive the session from the
 *   server—an independent REST call separate from the PowerSync stream.
 * - The `createEffect` that calls `ensureUserSettingsRow` ALREADY distinguishes
 *   guest from real account (guard `uid !== VISITOR_ID` added in
 *   `theme-provider.tsx`, see `docs/USER_SEED_GUARD.md`), but this does NOT close
 *   the window below: the guard prevents a competing local INSERT for a real
 *   account; it does not make that account's row (created by the SERVER,
 *   `packages/core-rust/src/db/user.rs:97-119`/`:350-395`) arrive faster
 *   via sync. `ensureUserSettingsRow` DOES prevent `UNIQUE constraint failed`
 *   between two guest tabs (its transaction remains live and is guarded by
 *   `ensure-user-settings-row.test.ts`), but both THAT INSERT (guest)
 *   and the server INSERT (real account) may be outside the view
 *   at the instant the CRUD queue empties and PowerSync rebuilds
 *   from a checkpoint that does not yet contain it. An UPDATE immediately afterward
 *   (choosing language/theme/font during registration, or the guest itself after its
 *   seed) falls into the SAME window as `bible_note`/`bible_highlight`. The
 *   ghost row from the local INSERT for a real account was a THIRD factor that
 *   worsened this window (one extra local writer competing for the same
 *   PK)—removing it (the guard) does not close the window, because the window belongs to
 *   PowerSync's checkpoint mechanism, not the source of the INSERT.
 *
 * Confirmed with deterministic red→green in `write-window.test.ts` (the
 * "user settings" block): with the old form (`.update(user).where(...)` written
 * by hand), all 6 mutations silently lose the value when the row is
 * hidden; with `patchRowVerified`, all 6 land. That test does not change with
 * the seeding guard: it still exercises the SAME window, now for both
 * row origins (a locally seeded guest and a real account synced
 * from the server).
 */

/**
 * Ensures the settings row for `userId` exists. Returns `true` if it
 * had to create it.
 *
 * Generic in its signature (it accepts any `userId`, which its own
 * tests cover), but its ONLY caller (`theme-provider.tsx`) restricts it to the
 * guest (`uid === VISITOR_ID`) as of 2026-08-13. A real account never
 * needs this seed: the server unconditionally creates its `user` row when
 * registrarse (`packages/core-rust/src/db/user.rs:97-119`/`:350-395`), y
 * seeding ANOTHER row locally for the same id was the ghost row this repo
 * closed—see `docs/USER_SEED_GUARD.md` for the full rationale, including
 * why the 6 mutations below remain protected by `patchRowVerified`
 * pese al guard.
 *
 * ── Why this lives here rather than inline in ThemeProvider ──────────────────
 * It used to be a handwritten "check whether it exists; if not, insert it" inside a
 * `createEffect`. This is a manual check-then-act: between the SELECT and INSERT
 * there is a round trip to the PowerSync worker (~90 ms measured), and during that gap
 * another run of the same effect may see the empty table and
 * insert as well (two guest tabs, or the home page chaining redirects). The
 * second run fails with `UNIQUE constraint failed: ps_data__user.id`.
 *
 * The fix is to put the check and write in the SAME write
 * transaction: PowerSync serializes the write lock (and does so in the shared
 * worker, so also across TABS), which means the second entrant
 * already sees the row and does not insert.
 *
 * **`ON CONFLICT DO NOTHING` does not work here, despite appearances.** PowerSync tables
 * are not tables: they are VIEWS with `INSTEAD OF` triggers, and SQLite
 * contesta `cannot UPSERT a view`. Medido en el navegador — el «arreglo obvio»
 * merely swaps one console error for another.
 */
export async function ensureUserSettingsRow(
  db: PowerSyncDrizzleDB,
  userId: string,
): Promise<boolean> {
  return db.transaction(async (tx) => {
    const existing = await tx
      .select({ id: user.id })
      .from(user)
      .where(eq(user.id, userId))
      .limit(1);
    if (existing.length > 0) {
      return false;
    }
    await tx.insert(user).values({
      id: userId,
      is_blur: 1,
      follow_device_theme: 1,
    });
    return true;
  });
}

/**
 * Shared Drizzle mutation for updating User locale.
 */
export async function updateUserLocaleMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  locale: string,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { locale });
}

/**
 * Shared Drizzle mutation for updating User font family preference.
 */
export async function updateUserFontFamilyMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  fontFamilyId: string | null,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { font_family: fontFamilyId });
}

/**
 * Writes the avatar KEY to the local row.
 *
 * It is written local-first (like language, font, and theme) rather than via REST, so
 * the new photo appears immediately and survives a cold reload without
 * a connection. The BINARY does go through REST (`POST /api/avatar`) because it does not fit in the
 * sync queue; only the key returned by that upload travels through here.
 *
 * `null` = "remove the photo" (the initials return).
 *
 * A guest has no synchronizable row of their own and cannot have an avatar (zero
 * rule), so there is no `VISITOR_ID` branch here as in `locale`/`theme`.
 */
export async function updateUserImageMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  imageKey: string | null,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { image: imageKey });
}

/**
 * Shared Drizzle mutation for updating global theme.
 */
export async function updateGlobalThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  themeId: string | null,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { global_theme_id: themeId });
}

/**
 * Shared Drizzle mutation for updating blur effect preference.
 */
export async function updateBlurMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  isBlur: boolean,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { is_blur: isBlur ? 1 : 0 });
}

/**
 * Shared Drizzle mutation for updating follow device appearance preference.
 */
export async function updateFollowDeviceThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  followDevice: boolean,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, {
    follow_device_theme: followDevice ? 1 : 0,
  });
}
